import { observer } from 'mobx-react-lite';
import React, { createContext, lazy, Suspense, useContext, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { AuthStore, AuthSupplier } from '../common/auth/auth.store';
import { styled } from '../common/common.styles';
import { wrapAsDefault } from '../common/components/loader/dwt-loadable';
import { AppErrorBoundary } from '../common/errors/app-error-boundary';
import { ErrorCode } from '../common/errors/error-codes';
import { ExternalName } from '../common/general/module/brand-loader';
import { TextDirection, TextDirectionContext } from '../common/styles/general/text-direction';
import { normalFont, Theme } from '../common/styles/general/theme';
import { EmbedStore } from '../store/embed-store';
import { rootStore } from '../store/root-store';
import { ModuleContext } from './context/module-context';
import { DWTComponentOptions } from './dwt-app';
import useGlobalTooltipObserver from '../common/components/tooltip/global-tooltip-observer';
import packageJson from '../../../package.json';

const LazyEmbeddedModuleInfo = lazy(() =>
  import('../common/components/module/embedded-module-info').then((m) => m.EmbeddedModuleInfo).then(wrapAsDefault)
);

const EmbeddedComponentRoot = styled.div<{ debugMode: boolean }>`
  font-family: ${normalFont};
  color: ${(p) => p.theme.color.base[100]};
  height: fit-content;
  /* For the AtAvagDemo page correction AtAvagDemoFontFix style was added */
  ${(p) => (p.debugMode ? 'border: 8px solid green' : '')}
`;

interface Props {
  options: DWTComponentOptions;
  moduleName: ExternalName;
  directionGetter: () => TextDirection;
}

export const RootStoreContext = createContext(rootStore);
export const TranslateContext = createContext(rootStore.localizationStore.translate);
export const AuthSupplierContext = createContext<AuthSupplier>(() => Promise.resolve({} as any));
export const EmbedContext = createContext<EmbedStore>(new EmbedStore());

export const EmbedRoot = observer((props: React.PropsWithChildren<Props>) => {
  const moduleContext = useContext(ModuleContext);
  const [embedStore] = useState(new EmbedStore());
  const [authStore] = useState(new AuthStore(embedStore));
  useGlobalTooltipObserver(props.options.brand || 'opelnew');

  useEffect(() => {
    embedStore.init(props.options, props.moduleName);
  }, [props.options]);
  useEffect(() => {
    if (moduleContext && moduleContext.module) {
      rootStore.localizationStore.loadLabels(
        moduleContext.module.labelPrefix,
        moduleContext.module.admin || false,
        authStore.getAuthInfo
      );
    }
  }, [moduleContext]);

  let cTimeout: any;

  const showVersionPopup = () => {
    if (!document.getElementById('version-popup')) {
      const popup = document.createElement('div');
      popup.id = 'version-popup';
      popup.style.position = 'fixed';
      popup.style.top = '10px';
      popup.style.right = '10px';
      popup.style.padding = '10px';
      popup.style.backgroundColor = 'white';
      popup.style.border = '1px solid #ccc';
      popup.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.1)';
      popup.style.zIndex = '1000';

      const versionText = document.createElement('p');
      versionText.textContent = `App verzió: ${packageJson.version}`;
      popup.appendChild(versionText);

      document.body.appendChild(popup);
      cTimeout = setTimeout(() => {
        hideVersionPopup();
      }, 5000);
    }
  };

  const hideVersionPopup = () => {
    const popup = document.getElementById('version-popup');
    if (popup) {
      document.body.removeChild(popup);
      clearTimeout(cTimeout);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'V') {
        showVersionPopup();
      }
    };

    const handleKeyUp = (event: any) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'V') {
        hideVersionPopup();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  if (!moduleContext || !moduleContext.module) {
    return null;
  }

  const theme: Theme = { ...moduleContext.basetheme };
  const translate = rootStore.localizationStore.translate;
  const showDebugModuleInfo = rootStore.urlStore.searchParams.devShowModules ?? false;
  return (
    <AppErrorBoundary name="EmbedRoot" errorCode={ErrorCode.ReactBoundaryEmbedRoot}>
      <AuthSupplierContext.Provider value={authStore.getAuthInfo}>
        <RootStoreContext.Provider value={rootStore}>
          <TranslateContext.Provider value={translate}>
            <EmbedContext.Provider value={embedStore}>
              <ThemeProvider theme={theme}>
                <TextDirectionContext.Provider value={props.directionGetter}>
                  <EmbeddedComponentRoot className="dwt-slider-container" debugMode={showDebugModuleInfo}>
                    {showDebugModuleInfo ? (
                      <Suspense fallback={null}>
                        <LazyEmbeddedModuleInfo embedStore={embedStore} />
                      </Suspense>
                    ) : null}
                    {props.children}
                  </EmbeddedComponentRoot>
                </TextDirectionContext.Provider>
              </ThemeProvider>
            </EmbedContext.Provider>
          </TranslateContext.Provider>
        </RootStoreContext.Provider>
      </AuthSupplierContext.Provider>
    </AppErrorBoundary>
  );
});
